import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Layers, CheckCircle2, CircleHelp, Download, Loader2, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkUpdateDialog } from "@/components/BulkUpdateDialog";
import { UpdatePolicies } from "@/components/UpdatePolicies";
import { SoftwareCatalog } from "@/components/SoftwareCatalog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtDate } from "@/lib/api";

export const STATUS = {
  healthy: ["Sain", "bg-emerald-500", "text-emerald-700", CheckCircle2],
  warning: ["Attention", "bg-amber-500", "text-amber-700", AlertTriangle],
  critical: ["Critique", "bg-red-500", "text-red-700", XCircle],
  unknown: ["En attente", "bg-slate-300", "text-slate-500", CircleHelp],
};

export const Bar = ({ v, testid }) => {
  if (v == null) return <span className="text-slate-400">—</span>;
  const tone = v >= 90 ? "bg-red-500" : v >= 75 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2 min-w-[90px]" data-testid={testid}>
      <div className="h-1.5 flex-1 rounded bg-slate-100"><div className={`h-1.5 rounded ${tone}`} style={{ width: `${Math.min(100, v)}%` }} /></div>
      <span className="text-xs tabular-nums text-slate-600 w-9 text-right">{Math.round(v)}%</span>
    </div>
  );
};

const Kpi = ({ label, value, tone = "text-slate-900", id }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={id}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
  </div>
);

const Fleet = ({ rows, onOpen, reload }) => {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [bulk, setBulk] = useState(false);
  const shown = rows.filter((r) => `${r.name} ${r.group} ${r.os}`.toLowerCase().includes(q.toLowerCase()));
  const allOn = shown.length > 0 && shown.every((r) => sel.includes(r.machine_id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const closeBulk = useCallback(() => setBulk(false), []);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input placeholder="Rechercher un poste, un client…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-white" data-testid="rmm-search" />
        {sel.length > 0 && (
          <Button onClick={() => setBulk(true)} data-testid="rmm-bulk-update-btn"><Layers className="h-4 w-4 mr-2" /> Mettre à jour la sélection ({sel.length})</Button>
        )}
        {sel.length > 0 && <button type="button" className="text-sm text-slate-500 hover:underline" onClick={() => setSel([])} data-testid="rmm-bulk-clear">Effacer la sélection</button>}
      </div>
      <BulkUpdateDialog ids={sel} open={bulk} onClose={closeBulk} onDone={() => { setBulk(false); setSel([]); setTimeout(reload, 3000); }} />
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full min-w-[980px] text-sm" data-testid="rmm-fleet-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="pl-3 w-8" onClick={(e) => e.stopPropagation()}><Checkbox checked={allOn} onCheckedChange={() => setSel(allOn ? [] : shown.map((r) => r.machine_id))} data-testid="rmm-select-all" /></th>{["État", "Poste", "Système", "CPU", "Mémoire", "Disque", "Antivirus", "Pare-feu", "Mises à jour", "Vu"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {!shown.length && <tr><td colSpan={11} className="py-10 text-center text-slate-500">Aucun poste. Les postes apparaissent dès qu'un agent est installé.</td></tr>}
            {shown.map((r) => {
              const [label, dot, txt] = STATUS[r.status];
              return (
                <tr key={r.machine_id} onClick={() => onOpen(r.machine_id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`rmm-row-${r.machine_id}`}>
                  <td className="pl-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(r.machine_id)} onCheckedChange={() => toggle(r.machine_id)} data-testid={`rmm-select-${r.machine_id}`} /></td>
                  <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1.5 text-xs font-medium ${txt}`}><span className={`h-2 w-2 rounded-full ${dot}`} />{label}</span></td>
                  <td className="px-3"><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.group || "—"}</p></td>
                  <td className="px-3 text-xs text-slate-600">{r.os_version || r.os}</td>
                  <td className="px-3"><Bar v={r.cpu} /></td>
                  <td className="px-3"><Bar v={r.ram} /></td>
                  <td className="px-3"><Bar v={r.disk} testid={`rmm-disk-${r.machine_id}`} /></td>
                  <td className="px-3 text-xs">{r.av ? <span className={r.av.enabled ? "text-emerald-700" : "text-red-600"}>{r.av.enabled ? "Actif" : "Désactivé"}</span> : "—"}</td>
                  <td className="px-3 text-xs">{r.firewall ? <span className={r.firewall.enabled ? "text-emerald-700" : "text-red-600"}>{r.firewall.enabled ? "Actif" : "Désactivé"}</span> : "—"}</td>
                  <td className="px-3 text-xs" data-testid={`rmm-updates-${r.machine_id}`}>
                    {r.updates_at ? (r.os_updates + r.app_updates ? <span className="font-medium text-amber-700">{r.os_updates} système · {r.app_updates} logiciels</span> : <span className="text-emerald-700">À jour</span>) : <span className="text-slate-400">Non analysé</span>}
                    {r.reboot_pending && <p className="text-[11px] text-amber-700">Redémarrage requis</p>}
                  </td>
                  <td className="px-3 text-xs">{r.online ? <span className="text-emerald-700">En ligne</span> : <span className="text-slate-500">{fmtDate(r.last_seen)}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Alerts = ({ alerts, reload }) => {
  const resolve = (id) => api.post(`/rmm/alerts/${id}/resolve`).then(() => { toast.success("Alerte clôturée"); reload(); }).catch((e) => toast.error(errMsg(e)));
  if (!alerts.length) return <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="rmm-alerts-empty"><ShieldCheck className="h-8 w-8 mx-auto mb-2 text-emerald-500" />Aucune alerte en cours.</div>;
  return (
    <div className="rounded-lg border border-slate-200 bg-white divide-y" data-testid="rmm-alerts-list">
      {alerts.map((a) => (
        <div key={a.alert_id} className="flex items-center gap-4 px-4 py-3 text-sm" data-testid={`rmm-alert-${a.alert_id}`}>
          <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${["disk_low", "av_off"].includes(a.kind) ? "text-red-500" : "text-amber-500"}`} />
          <div className="flex-1 min-w-0">
            <p className="font-medium">{a.label} — <Link to={`/app/rmm/${a.machine_id}`} className="text-blue-600 hover:underline">{a.machine_name}</Link></p>
            <p className="text-xs text-slate-500 truncate">{a.message} · depuis le {fmtDate(a.at)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => resolve(a.alert_id)} data-testid={`rmm-alert-resolve-${a.alert_id}`}>Clôturer</Button>
        </div>
      ))}
    </div>
  );
};

export default function Rmm() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/rmm/overview").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/rmm/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const scanAll = async () => {
    const online = d.machines.filter((m) => m.online);
    if (!online.length) return toast.error("Aucun poste en ligne");
    setBusy(true);
    await Promise.allSettled(online.map((m) => api.post(`/rmm/machines/${m.machine_id}/scan`)));
    setBusy(false);
    toast.success(`Recherche de mises à jour lancée sur ${online.length} poste(s)`);
    setTimeout(load, 4000);
  };

  if (!d) return <div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const k = d.kpis;
  return (
    <div className="flex-1 min-w-0 overflow-auto bg-slate-50 p-6 lg:p-8 space-y-6" data-testid="rmm-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Activity className="h-6 w-6 text-blue-600" /> RMM — Supervision du parc</h1>
          <p className="text-sm text-slate-500">Santé, inventaire et mises à jour de tous vos postes. Rien n'est installé sans votre validation.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="rmm-refresh-btn"><RefreshCw className="h-4 w-4 mr-2" /> Actualiser</Button>
        <Button onClick={scanAll} disabled={busy} data-testid="rmm-scan-all-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Rechercher les mises à jour</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi label="Postes" value={`${k.online}/${k.total}`} id="rmm-kpi-total" />
        <Kpi label="Sains" value={k.healthy} tone="text-emerald-600" id="rmm-kpi-healthy" />
        <Kpi label="Attention" value={k.warning} tone="text-amber-600" id="rmm-kpi-warning" />
        <Kpi label="Critiques" value={k.critical} tone="text-red-600" id="rmm-kpi-critical" />
        <Kpi label="MàJ système" value={k.os_updates} id="rmm-kpi-os" />
        <Kpi label="MàJ logiciels" value={k.app_updates} id="rmm-kpi-apps" />
        <Kpi label="Alertes" value={k.alerts} tone={k.alerts ? "text-red-600" : "text-slate-900"} id="rmm-kpi-alerts" />
      </div>
      <Tabs defaultValue="fleet">
        <TabsList>
          <TabsTrigger value="fleet" data-testid="rmm-tab-fleet">Parc</TabsTrigger>
          <TabsTrigger value="software" data-testid="rmm-tab-software">Installer des logiciels</TabsTrigger>
          <TabsTrigger value="policies" data-testid="rmm-tab-policies">Mises à jour automatiques</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="rmm-tab-alerts">Alertes{alerts.length ? ` (${alerts.length})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="fleet"><Fleet rows={d.machines} onOpen={(id) => nav(`/app/rmm/${id}`)} reload={load} /></TabsContent>
        <TabsContent value="software"><SoftwareCatalog rows={d.machines} /></TabsContent>
        <TabsContent value="policies"><UpdatePolicies /></TabsContent>
        <TabsContent value="alerts"><Alerts alerts={alerts} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
